import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  BellIcon,
  GitCommitHorizontalIcon,
  PlugIcon,
  SettingsIcon,
  UserRoundIcon,
} from 'lucide-react';

import {
  NOTIFICATIONS_PATH,
  NotificationBell,
  PAGE_COPY,
  SETTINGS_PATH,
} from '@/features/notifications';
import {
  ACCOUNT_COPY,
  ACCOUNT_SETTINGS_PATH,
  COMMITS_COPY,
  COMMITS_SETTINGS_PATH,
} from '@/features/identity';
import { EDITOR_COPY, EDITOR_SETTINGS_PATH } from '@/features/editor-access';

import { navItemClasses } from '../navItemClasses';
import { NavRail } from './NavRail';
import { ShellFrame } from './ShellFrame';

/**
 * The personal area's destinations: only what exists (spec 142). The inbox
 * stands apart from the settings (spec 239): it is what arrived, not a
 * setting, and sits above the settings that decide what arrives (spec 147).
 */
// Functions, not constants: this module is reached while the notifications
// package is still loading (it imports the account menu, which imports this
// package), so its copy is only safe to read at render time.
function inboxNav() {
  return [
    {
      label: PAGE_COPY.title,
      to: NOTIFICATIONS_PATH,
      icon: BellIcon,
    },
  ] as const;
}

function settingsNav() {
  return [
    // Who you are here; where Settings opens (spec 239).
    {
      label: ACCOUNT_COPY.nav,
      to: ACCOUNT_SETTINGS_PATH,
      icon: UserRoundIcon,
    },
    {
      label: PAGE_COPY.settings,
      to: SETTINGS_PATH,
      icon: SettingsIcon,
    },
    // How the agent's commits name you (spec 158).
    {
      label: COMMITS_COPY.nav,
      to: COMMITS_SETTINGS_PATH,
      icon: GitCommitHorizontalIcon,
    },
    // Connecting Claude Code and other MCP editors (spec 236).
    {
      label: EDITOR_COPY.nav,
      to: EDITOR_SETTINGS_PATH,
      icon: PlugIcon,
    },
  ] as const;
}

/** The rail's group heading, and the name its navigation is announced by. */
const SETTINGS_LABEL = 'Settings';

/**
 * The personal settings' shell (spec 142): `ShellFrame` filled with what
 * belongs to the person rather than to a workspace or the organization. The
 * third of the frame's callers, beside the workspace and the console.
 */
export function SettingsShell({ children }: { children: ReactNode }) {
  return (
    <ShellFrame
      rail={(collapsed) => (
        <NavRail
          collapsed={collapsed}
          context={<Context collapsed={collapsed} />}
        >
          <nav aria-label="Inbox" className="flex flex-col gap-0.5">
            {inboxNav().map((item) => (
              <RailLink key={item.to} item={item} collapsed={collapsed} />
            ))}
          </nav>
          {/* Headed as the workspace sidebar heads its second group; the
              heading folds away with the rail, the navigation keeps its name. */}
          <nav aria-label={SETTINGS_LABEL} className="flex flex-col gap-1">
            {collapsed ? null : (
              <p className="text-muted-foreground px-2.5 text-2xs font-medium tracking-wide uppercase">
                {SETTINGS_LABEL}
              </p>
            )}
            <div className="flex flex-col gap-0.5">
              {settingsNav().map((item) => (
                <RailLink key={item.to} item={item} collapsed={collapsed} />
              ))}
            </div>
          </nav>
        </NavRail>
      )}
      topbar={
        <>
          <span className="text-muted-foreground flex-1 text-sm">
            Your settings
          </span>
          <NotificationBell />
        </>
      }
    >
      {children}
    </ShellFrame>
  );
}

function RailLink({
  item,
  collapsed,
}: {
  item: ReturnType<typeof inboxNav | typeof settingsNav>[number];
  collapsed: boolean;
}) {
  return (
    <Link
      to={item.to}
      title={collapsed ? item.label : undefined}
      className={navItemClasses({ collapsed })}
    >
      <item.icon
        aria-hidden="true"
        className="size-4 shrink-0"
        strokeWidth={1.5}
      />
      {collapsed ? <span className="sr-only">{item.label}</span> : item.label}
    </Link>
  );
}

/** Whose settings, and the way back to the workspaces. */
function Context({ collapsed }: { collapsed: boolean }) {
  if (collapsed) {
    return (
      <div className="flex justify-center">
        <UserRoundIcon
          aria-hidden="true"
          className="text-sidebar-foreground/60 size-4"
          strokeWidth={1.5}
        />
        <span className="sr-only">Your settings</span>
      </div>
    );
  }
  return (
    <div className="border-sidebar-border flex flex-col gap-1 rounded-md border px-2.5 py-2">
      <span className="flex items-center gap-2 text-sm font-medium">
        <UserRoundIcon
          aria-hidden="true"
          className="text-sidebar-foreground/60 size-4 shrink-0"
          strokeWidth={1.5}
        />
        Your settings
      </span>
      <Link
        to="/"
        className="text-muted-foreground hover:text-foreground focus-visible:ring-sidebar-ring flex items-center gap-1.5 rounded-sm text-xs transition-colors focus-visible:ring-3 focus-visible:outline-none"
      >
        <ArrowLeftIcon
          aria-hidden="true"
          className="size-3.5 shrink-0"
          strokeWidth={1.5}
        />
        Workspaces
      </Link>
    </div>
  );
}
